"use client";

import React, { useState } from 'react';
import { cn } from "@/lib/utils";
import Link from "next/link";
import { Menu, X } from 'lucide-react';
import { usePathname } from 'next/navigation';
import UserNav from './navigation/UserNav';
import { AnimatePresence, motion } from 'framer-motion';

const TopBar = () => {
  const pathname = usePathname();
  const isDevSite = pathname?.startsWith('/dev');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  return (
    <header className={`
      fixed top-0 left-0 right-0 z-50
      bg-black/75 dark:bg-black/90
      backdrop-blur-2xl
      border-b border-white/10
      transition-all duration-300 ease-in-out
      shadow-xl shadow-black/40
    `}>
      <div className="mx-auto px-4 sm:px-6 lg:px-8 h-16" style={{ maxWidth: "1280px" }}>
        <div className="flex justify-between items-center h-16">
          {/* Left side with logo and links */}
          <div className="flex items-center gap-8">
            <Link 
              href="/" 
              className={`
                font-bold text-xl
                bg-clip-text text-transparent 
                bg-gradient-to-r from-blue-400 to-cyan-400
                hover:from-blue-300 hover:to-cyan-300
                transition-colors
              `}
            >
              JIGPX
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link
                href="/dev"
                className={`
                  text-sm font-medium
                  text-amber-400 hover:text-amber-300
                  transition-colors
                `}
              >
                Dev
              </Link>
            </div>
          </div>

          {/* Right side menu */}
          <div className="flex items-center gap-4">
            <UserNav 
              isOpen={isUserMenuOpen}
              onOpenChange={setIsUserMenuOpen}
            />

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-300"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        <AnimatePresence mode="wait">
          {isMobileMenuOpen && (
            <>
              {/* Backdrop - add backdrop for smoother feel */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="fixed inset-0 top-16 z-30 bg-black/20 backdrop-blur-sm md:hidden"
                onClick={() => setIsMobileMenuOpen(false)}
              />
            
              {/* Menu container with fixed positioning and improved animation */}
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ 
                  opacity: 0,
                  height: 0
                }}
                transition={{
                  height: { 
                    type: "tween", 
                    duration: 0.3,
                    ease: [0.04, 0.62, 0.23, 0.98] // Custom easing for smoother animation
                  },
                  opacity: { duration: 0.2 }
                }}
                className={`
                  md:hidden
                  fixed top-16 left-0 right-0
                  mx-auto px-4 sm:px-6 lg:px-8
                  bg-black/90
                  backdrop-blur-2xl
                  border-t border-white/10
                  shadow-xl shadow-black/40
                  overflow-hidden
                  z-40
                `}
                style={{ maxWidth: "1280px" }} // Match parent container width
              >
                <div className="py-4 space-y-4">
                  <Link
                    href="/dev"
                    className={`
                      block px-4 py-2 rounded-md
                      text-sm font-medium
                      text-amber-400 hover:text-amber-300
                      transition-colors
                    `}
                  >
                    Dev
                  </Link>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default TopBar;